import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import ClientLink from "@/components/ClientLink";
import { StatusBadge } from "@/components/StatusBadge";
import CoachCard from "@/components/CoachCard";
import BrainPanel from "@/components/BrainPanel";
import GoalTrackerCard from "@/components/GoalTrackerCard";
import { api } from "@/lib/api";
import {
  fmtEUR, fmtNum, fmtDateIT, fmtDateTimeIT, statoClienteVariant, resolveStatoColor,
  statoPraticaVariant, monthLabelIT,
} from "@/lib/format";
import {
  Users, AlertTriangle, CalendarClock, Briefcase, TrendingUp, Phone,
  Flame, Clock3, ArrowUpRight, UserCheck, Megaphone, FileText, Handshake,
  ArrowRight, Loader2, MessageCircle, ChevronRight, Sparkles,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, AreaChart, Area,
} from "recharts";
import { useAuth } from "@/contexts/AuthContext";
import { useCountUp } from "@/lib/useCountUp";
import { DashboardSkeleton } from "@/components/SkeletonLoader";
import OnboardingWelcome from "@/components/OnboardingWelcome";
import EmptyState from "@/components/ui/EmptyState";
import LoadingBlock from "@/components/ui/LoadingBlock";

/* ─────────────────────────────────────────────
   UTILITY
───────────────────────────────────────────── */
function ViewAsSwitcher({ teamMembers, viewAs, setViewAs, currentRole }) {
  if (!teamMembers || teamMembers.length === 0) return null;
  const agents  = teamMembers.filter((u) => u.role === "agent");
  const leaders = teamMembers.filter((u) => u.role === "leader");
  const waOps   = teamMembers.filter((u) => u.role === "wa_operator");
  const labelGlobal = currentRole === "leader" ? "Il mio team" : "Team globale";
  return (
    <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-[2px] pl-3 pr-1 py-1.5" data-testid="view-as-switcher">
      <span className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">Vista</span>
      <select
        value={viewAs}
        onChange={(e) => setViewAs(e.target.value)}
        data-testid="view-as-select"
        className="text-sm bg-transparent outline-none pr-2 font-medium text-slate-800 cursor-pointer"
      >
        <option value="">{labelGlobal}</option>
        {agents.length > 0 && <optgroup label="Agenti">{agents.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}</optgroup>}
        {leaders.length > 0 && <optgroup label="Team Leader">{leaders.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}</optgroup>}
        {waOps.length > 0 && <optgroup label="Op. WhatsApp">{waOps.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}</optgroup>}
      </select>
    </div>
  );
}

/* ─────────────────────────────────────────────
   KPI CARD — Apple-level, generous whitespace
───────────────────────────────────────────── */
function KpiCard({ label, value, sub, icon: Icon, accent = false, testid, to, warn = false, trend, tone }) {
  const Tag = to ? Link : "div";
  const extra = to ? { to } : {};

  const isWarn   = warn && value > 0;
  const isAccent = accent && !isWarn;
  const isNum    = typeof value === "number" && Number.isFinite(value);
  const animated = useCountUp(isNum ? value : 0);

  // Tono vivace (Monday-style): colora barra top, icona e numero
  const toneHex = isWarn ? "#E2445C" : (tone || (isAccent ? "#B7955B" : null));

  const borderColor = toneHex ? `${toneHex}40` : "#E8ECF2";
  const iconBg      = toneHex
    ? `linear-gradient(135deg, ${toneHex} 0%, ${toneHex}CC 100%)`
    : "linear-gradient(135deg, #F4F6FA 0%, #E8ECF2 100%)";
  const iconColor   = toneHex ? "#FFFFFF" : "#475569";
  const topBarColor = toneHex || "var(--gold)";

  return (
    <Tag
      {...extra}
      data-testid={testid}
      className={`kpi-premium group relative overflow-hidden flex flex-col justify-between p-6 ${to ? "cursor-pointer" : ""}`}
      style={{
        background: "linear-gradient(180deg, #FFFFFF 0%, #FAFBFC 100%)",
        border: `1px solid ${borderColor}`,
        borderRadius: 6,
        boxShadow: "0 1px 2px rgba(10,25,47,0.04), inset 0 1px 0 rgba(255,255,255,0.7)",
        transition: "transform 260ms cubic-bezier(0.16,1,0.3,1), box-shadow 260ms ease, border-color 260ms ease",
      }}
    >
      {/* Barra colorata top — sempre visibile con tono vivace */}
      <span
        aria-hidden="true"
        className="absolute top-0 left-0 h-[3px] transition-all duration-500 ease-out"
        style={{
          width: toneHex ? "100%" : "0%",
          background: toneHex
            ? `linear-gradient(90deg, ${toneHex} 0%, ${toneHex}99 100%)`
            : `linear-gradient(90deg, transparent 0%, ${topBarColor} 30%, ${topBarColor} 70%, transparent 100%)`,
          boxShadow: `0 0 12px ${toneHex ? `${toneHex}66` : "rgba(183,149,91,0.55)"}`,
        }}
      />
      {/* Alone colorato angolo destro top */}
      <span
        aria-hidden="true"
        className={`absolute top-0 right-0 w-20 h-20 pointer-events-none transition-opacity duration-300 ${toneHex ? "opacity-70 group-hover:opacity-100" : "opacity-0 group-hover:opacity-100"}`}
        style={{
          background: `radial-gradient(circle at top right, ${toneHex ? `${toneHex}1F` : "rgba(183,149,91,0.14)"} 0%, transparent 70%)`,
        }}
      />

      {/* Top row */}
      <div className="flex items-start justify-between mb-4 relative z-[1]">
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">{label}</p>
        {Icon && (
          <div
            className="w-10 h-10 flex items-center justify-center shrink-0 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3"
            style={{
              background: iconBg,
              borderRadius: 8,
              boxShadow: toneHex
                ? `0 4px 12px ${toneHex}55, inset 0 1px 0 rgba(255,255,255,0.25)`
                : "inset 0 1px 0 rgba(255,255,255,0.8), 0 2px 4px rgba(10,25,47,0.06)",
            }}
          >
            <Icon className="w-5 h-5" strokeWidth={1.5} style={{ color: iconColor }} />
          </div>
        )}
      </div>

      {/* Value */}
      <div className="relative z-[1]">
        <p
          className="font-serif tabular-nums leading-none"
          style={{
            fontSize: "2.5rem",
            fontWeight: 300,
            color: toneHex || "#0D1829",
            letterSpacing: "-0.03em",
            fontVariantNumeric: "tabular-nums oldstyle-nums",
          }}
        >
          {isNum ? animated.toLocaleString("it-IT") : value}
        </p>
        {sub && (
          <p className="text-[11px] text-slate-500 mt-3 tabular-nums leading-none uppercase tracking-wider">
            {sub}
          </p>
        )}
      </div>

      {/* CTA on hover */}
      {to && (
        <div
          className="absolute bottom-4 right-5 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.15em] opacity-0 group-hover:opacity-100 transition-all duration-300 group-hover:translate-x-0 translate-x-[-4px]"
          style={{ color: toneHex || "var(--gold)" }}
        >
          Apri <ChevronRight className="w-3 h-3" />
        </div>
      )}
    </Tag>
  );
}

/* ─────────────────────────────────────────────
   SECTION HEADER
───────────────────────────────────────────── */
function SectionHeader({ title, action }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <h2
        className="font-serif font-light tracking-tight text-slate-900"
        style={{ fontSize: "1.1rem" }}
      >
        {title}
      </h2>
      {action && (
        <div className="text-[11px] font-medium" style={{ color: "var(--gold)" }}>
          {action}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   SURFACE CARD
───────────────────────────────────────────── */
function Surface({ children, testid, className = "", noPad = false }) {
  return (
    <div
      className={`surface-lux ${noPad ? "" : "p-6"} ${className}`}
      data-testid={testid}
    >
      {children}
    </div>
  );
}

/* ─────────────────────────────────────────────
   METRIC ROW (inside Surface)
───────────────────────────────────────────── */
function MetricRow({ label, value, sub, accent = false, last = false }) {
  return (
    <div
      className={`flex items-center justify-between py-4 ${last ? "" : "border-b"}`}
      style={{ borderColor: "#F0F2F5" }}
    >
      <div>
        <p className="text-[12px] font-medium text-slate-500">{label}</p>
        {sub && <p className="text-[11px] text-slate-400 mt-0.5 tabular-nums">{sub}</p>}
      </div>
      <p
        className="font-serif tabular-nums text-right"
        style={{ fontSize: "1.25rem", fontWeight: 300, color: accent ? "#A18048" : "#0D1829" }}
      >
        {value}
      </p>
    </div>
  );
}

/* ─────────────────────────────────────────────
   APPOINTMENT ITEM
───────────────────────────────────────────── */
function AppointmentItem({ a, last = false }) {
  return (
    <div
      className={`flex items-center gap-4 py-4 ${last ? "" : "border-b"}`}
      style={{ borderColor: "#F0F2F5" }}
    >
      <div
        className="w-10 h-10 flex items-center justify-center shrink-0 font-serif text-base font-light"
        style={{ background: "#F5EFE3", borderRadius: 3, color: "#A18048" }}
      >
        {new Date(a.when).getDate()}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-medium text-slate-800 truncate">{a.title || "Appuntamento"}</p>
        <p className="text-[11px] text-slate-400 tabular-nums mt-0.5">{fmtDateTimeIT(a.when)}</p>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   PIPELINE ITEM
───────────────────────────────────────────── */
function PipelineItem({ label, cat, data: p = {}, last = false }) {
  const colors = { PP: "#B7955B", CQ: "#2563EB", LIQ: "#16A34A" };
  const c = colors[cat] || "#64748B";
  const pct = p.montante > 0 ? Math.min(100, Math.round((p.provvigioni / p.montante) * 100)) : 0;

  return (
    <div className={`py-5 ${last ? "" : "border-b"}`} style={{ borderColor: "#F0F2F5" }}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2.5">
          <div className="w-2 h-2 rounded-full" style={{ background: c }} />
          <span className="text-[13px] font-medium text-slate-800">{label}</span>
        </div>
        <span className="text-[12px] text-slate-400 tabular-nums">{p.count || 0} pratiche</span>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.12em] text-slate-400 mb-1">Montante</p>
          <p className="text-[13px] font-medium text-slate-800 tabular-nums">{fmtEUR(p.montante)}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.12em] text-slate-400 mb-1">Provvigioni</p>
          <p className="text-[13px] font-medium tabular-nums" style={{ color: c }}>{fmtEUR(p.provvigioni)}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.12em] text-slate-400 mb-1">Forecast</p>
          <p className="text-[13px] font-medium text-slate-800 tabular-nums">{fmtEUR(p.forecast)}</p>
        </div>
      </div>
      {/* Progress bar */}
      <div className="mt-3 h-1 rounded-full overflow-hidden" style={{ background: "#F0F2F5" }}>
        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: c }} />
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   HOT CLIENT ROW
───────────────────────────────────────────── */
function HotClientRow({ c, last = false }) {
  const r = resolveStatoColor(c.stato);
  return (
    <div
      className={`flex items-center gap-4 py-4 ${last ? "" : "border-b"}`}
      style={{ borderColor: "#F0F2F5" }}
    >
      <div
        className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-semibold shrink-0"
        style={{ background: "#F4F6FA", color: "#64748B" }}
      >
        {(c.cognome || "?").slice(0, 1).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <ClientLink clientId={c.id} variant="strong">
          <span className="text-[13px] font-medium text-slate-800 hover:text-slate-900">{c.cognome} {c.nome}</span>
        </ClientLink>
        <p className="text-[11px] text-slate-400 mt-0.5 tabular-nums">{c.telefono || "—"} · {fmtDateIT(c.data)}</p>
      </div>
      <StatusBadge variant={r.variant} customColor={r.hex}>{c.stato || "—"}</StatusBadge>
    </div>
  );
}

/* ─────────────────────────────────────────────
   CUSTOM TOOLTIP for chart
───────────────────────────────────────────── */
function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 px-4 py-3 shadow-lg" style={{ borderRadius: 4 }}>
      <p className="text-[11px] uppercase tracking-wider text-slate-400 mb-1">{label}</p>
      <p className="font-serif text-lg font-light text-slate-900">{fmtEUR(payload[0].value)}</p>
    </div>
  );
}

/* ─────────────────────────────────────────────
   SHORTCUT CARD
───────────────────────────────────────────── */
function ShortcutCard({ to, icon: Icon, title, description, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="group flex items-center gap-4 bg-white p-5 transition-all duration-200"
      style={{ border: "1px solid #E8ECF2", borderRadius: 4, boxShadow: "0 1px 2px rgba(10,25,47,0.04)" }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 8px 24px rgba(10,25,47,0.10)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 1px 2px rgba(10,25,47,0.04)"; e.currentTarget.style.transform = ""; }}
    >
      <div
        className="w-10 h-10 flex items-center justify-center shrink-0"
        style={{ background: "rgba(183,149,91,0.10)", borderRadius: 3 }}
      >
        <Icon className="w-5 h-5" strokeWidth={1.5} style={{ color: "#A18048" }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-semibold text-slate-800">{title}</p>
        <p className="text-[12px] text-slate-400 mt-0.5 truncate">{description}</p>
      </div>
      <ChevronRight
        className="w-4 h-4 text-slate-300 shrink-0 transition-transform group-hover:translate-x-0.5"
        strokeWidth={1.5}
      />
    </Link>
  );
}

/* ═══════════════════════════════════════════════
   ADMIN DASHBOARD
═══════════════════════════════════════════════ */
export default function DashboardPage() {
  const { user } = useAuth();
  const role = user?.role || "admin";
  const [data, setData]       = useState(null);
  const [meKpi, setMeKpi]     = useState(null);
  const [err, setErr]         = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const viewAs    = searchParams.get("viewAs") || "";
  const setViewAs = (uid) => {
    const p = new URLSearchParams(searchParams);
    if (uid) p.set("viewAs", uid); else p.delete("viewAs");
    setSearchParams(p);
  };
  const [teamMembers, setTeamMembers] = useState([]);
  const [showOnboarding, setShowOnboarding] = useState(() => {
    try { return !localStorage.getItem("ba_onboarding_dismissed"); } catch { return true; }
  });

  useEffect(() => {
    if (role === "admin" || role === "leader") {
      api.get("/team/users").then((r) => setTeamMembers(r.data.items || [])).catch(() => {});
    }
  }, [role]);

  useEffect(() => {
    if (role === "admin") {
      const impersonated = teamMembers.find((u) => u.id === viewAs);
      if (impersonated && impersonated.role === "wa_operator") { setData(null); setErr(""); return; }
      const url = viewAs ? `/dashboard/overview?viewAs=${viewAs}` : "/dashboard/overview";
      api.get(url).then((r) => setData(r.data)).catch((e) => setErr(e?.response?.data?.detail || "Errore caricamento"));
    } else if (role === "leader") {
      const impersonated = teamMembers.find((u) => u.id === viewAs);
      if (impersonated && impersonated.role === "wa_operator") { setMeKpi(null); setErr(""); return; }
      const url = viewAs ? `/dashboard/me?viewAs=${viewAs}` : "/dashboard/me";
      api.get(url).then((r) => setMeKpi(r.data.kpi || {})).catch((e) => setErr(e?.response?.data?.detail || "Errore caricamento"));
    } else {
      api.get("/dashboard/me").then((r) => setMeKpi(r.data.kpi || {})).catch((e) => setErr(e?.response?.data?.detail || "Errore caricamento"));
    }
  }, [role, viewAs, teamMembers]);

  const impersonatedUser = teamMembers.find((u) => u.id === viewAs);
  if (role === "wa_operator") return <WaOperatorDashboard user={user} />;
  if ((role === "admin" || role === "leader") && impersonatedUser?.role === "wa_operator") {
    return <WaOperatorDashboard user={impersonatedUser} viewAsId={impersonatedUser.id} switcher={<ViewAsSwitcher teamMembers={teamMembers} viewAs={viewAs} setViewAs={setViewAs} currentRole={role} />} />;
  }

  if (role !== "admin") {
    if (err) return <AppLayout title="Dashboard"><p className="text-rose-600 text-sm">{err}</p></AppLayout>;
    if (!meKpi) return <AppLayout title="Dashboard"><DashboardSkeleton /></AppLayout>;
    return <AgentDashboard user={user} kpi={meKpi} switcher={role === "leader" ? <ViewAsSwitcher teamMembers={teamMembers} viewAs={viewAs} setViewAs={setViewAs} currentRole={role} /> : null} />;
  }

  if (err) return <AppLayout title="Dashboard"><p className="text-rose-600 text-sm">{err}</p></AppLayout>;
  if (!data) return <AppLayout title="Console Direzionale" subtitle="Console direzionale"><DashboardSkeleton /></AppLayout>;

  const k        = data.kpi || {};
  const trend    = (data.monthlyTrend || []).map(m => ({ ...m, label: monthLabelIT(m.month).slice(0, 3) }));
  const realized = data.realized || {};
  const pancia   = data.pancia || {};
  const monthLabel = monthLabelIT(data.monthKey);

  return (
    <AppLayout
      title="Console Direzionale"
      subtitle={`${monthLabel} · Panoramica team`}
      actions={
        <div className="flex items-center gap-2">
          <ViewAsSwitcher teamMembers={teamMembers} viewAs={viewAs} setViewAs={setViewAs} currentRole={role} />
          <Link to="/team-performance" data-testid="dash-team-performance"
            className="bg-white border border-slate-200 hover:border-slate-300 text-[13px] font-medium px-3.5 py-2 flex items-center gap-2 transition-colors"
            style={{ borderRadius: 3, color: "#334155" }}>
            <UserCheck className="w-3.5 h-3.5" strokeWidth={1.6} /> Team
          </Link>
          <Link to="/clienti?view=daily" data-testid="dash-cta-daily"
            className="text-white text-[13px] font-medium px-4 py-2 flex items-center gap-2 transition-colors"
            style={{ background: "var(--navy)", borderRadius: 3 }}
            onMouseEnter={e => e.currentTarget.style.background = "var(--navy-3)"}
            onMouseLeave={e => e.currentTarget.style.background = "var(--navy)"}
          >
            <Phone className="w-3.5 h-3.5" strokeWidth={1.6} /> Richiami di oggi
          </Link>
        </div>
      }
    >
      {/* ── Scope banner ── */}
      {data.scope?.impersonating ? (
        <div
          className="flex items-center gap-3 px-5 py-3.5 mb-6"
          style={{ background: "#FDFAF5", border: "1px solid rgba(183,149,91,0.3)", borderRadius: 4 }}
          data-testid="dash-scope-banner"
        >
          <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold shrink-0"
            style={{ background: "var(--gold)", color: "var(--navy)" }}>
            {(data.scope.userName || "?").slice(0, 1).toUpperCase()}
          </div>
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-[0.18em] font-semibold" style={{ color: "#A18048" }}>{data.scope.label}</p>
            <p className="text-[13px] text-slate-600 mt-0.5 text-slate-800">Stai visualizzando i dati di <strong >{data.scope.userName}</strong> · {fmtNum(k.clientsTotal)} clienti assegnati</p>
          </div>
          <button onClick={() => setViewAs("")} data-testid="btn-exit-impersonation"
            className="text-[12px] font-medium px-3 py-1.5 transition-colors"
            style={{ background: "white", border: "1px solid rgba(183,149,91,0.4)", borderRadius: 3, color: "#A18048" }}
            onMouseEnter={e => e.currentTarget.style.background = "#FDFAF5"}
            onMouseLeave={e => e.currentTarget.style.background = "white"}>
            Torna al team
          </button>
        </div>
      ) : (
        <div
          className="flex items-center gap-3 px-5 py-3.5 mb-6"
          style={{ background: "#F6F8FB", border: "1px solid #E8ECF2", borderRadius: 4 }}
          data-testid="dash-scope-banner"
        >
          <Briefcase className="w-4 h-4 shrink-0" style={{ color: "var(--gold)" }} strokeWidth={1.6} />
          <p className="text-[13px] text-slate-500">
            <span className="font-semibold text-slate-700">Vista aggregata</span> · {fmtNum(k.clientsTotal)} clienti gestiti dal team
          </p>
        </div>
      )}

      {/* ── Onboarding (shows only when no data yet) ── */}
      {showOnboarding && k.clientsTotal === 0 && (
        <OnboardingWelcome userName={user?.name?.split(" ")[0]} onDismiss={() => setShowOnboarding(false)} />
      )}

      {/* ── KPI GRID — 4+4 ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 stagger animate-fade-in">
        <KpiCard testid="kpi-clientsToday"    label="Richiami oggi"      value={k.clientsToday    || 0} sub={`Su ${fmtNum(k.clientsTotal)} clienti`} icon={Phone}         tone="#579BFC" to="/clienti?view=daily" />
        <KpiCard testid="kpi-clientsExpired"  label="Clienti scaduti"    value={k.clientsExpired  || 0} sub="Data superata"                           icon={AlertTriangle}  warn   to="/clienti?view=expired" />
        <KpiCard testid="kpi-clientsHot"      label="Clienti caldi"      value={k.clientsHot      || 0} sub="Interessati · Da proporre"               icon={Flame}          tone="#FDAB3D" to="/clienti?view=hot" />
        <KpiCard testid="kpi-remindersOverdue" label="Promemoria scaduti" value={k.remindersOverdue|| 0} sub={`Prossimi 7gg: ${k.remindersNext7 || 0}`} icon={Clock3}        warn   to="/agenda" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 stagger animate-fade-in">
        <KpiCard testid="kpi-attesa-doc" label="Attesa documenti" value={k.clientsAttesaDoc  || 0} sub="In raccolta"        icon={FileText}   tone="#4ECCC6" to="/clienti?view=attesaDoc" />
        <KpiCard testid="kpi-trattativa" label="In trattativa"    value={k.clientsTrattativa || 0} sub="Negoziazione attiva" icon={Handshake}  tone="#A25DDC" to="/clienti?view=trattativa" />
        <KpiCard testid="kpi-no-date"    label="Senza data"        value={k.clientsNoDate     || 0} sub="Da pianificare"    icon={CalendarClock} tone="#FF158A" to="/clienti?view=noDate" />
        <KpiCard testid="kpi-clientsTot" label="Totale clienti"   value={k.clientsTotal      || 0} sub="Pool completo"      icon={Users}      tone="#00C875" to="/clienti" />
      </div>

      {/* ── FINANCIALS ROW ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        {/* Liquidato */}
        <Surface testid="dash-realized">
          <div className="flex items-start justify-between mb-6">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-600">Liquidato · Reale</p>
              <p className="text-[11px] text-slate-400 mt-0.5">{monthLabel}</p>
            </div>
            <div className="w-8 h-8 flex items-center justify-center" style={{ background: "#ECFDF5", borderRadius: 3 }}>
              <TrendingUp className="w-4 h-4 text-emerald-600" strokeWidth={1.6} />
            </div>
          </div>
          <p className="font-serif tabular-nums text-emerald-700" style={{ fontSize: "2rem", fontWeight: 300, letterSpacing: "-0.02em" }}>
            {fmtEUR(realized.provvigioni || 0)}
          </p>
          <div className="mt-4 pt-4 border-t" style={{ borderColor: "#F0F2F5" }}>
            <div className="flex items-center justify-between text-[12px]">
              <span className="text-slate-400">{realized.count || 0} pratiche</span>
              <span className="text-slate-500 tabular-nums">Montante {fmtEUR(realized.montante || 0)}</span>
            </div>
          </div>
        </Surface>

        {/* In pancia */}
        <Surface testid="dash-pancia">
          <div className="flex items-start justify-between mb-6">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em]" style={{ color: "#A18048" }}>In Pancia · Potenziale</p>
              <p className="text-[11px] text-slate-400 mt-0.5">{monthLabel}</p>
            </div>
            <div className="w-8 h-8 flex items-center justify-center" style={{ background: "#FDFAF5", borderRadius: 3 }}>
              <Briefcase className="w-4 h-4" strokeWidth={1.6} style={{ color: "#A18048" }} />
            </div>
          </div>
          <p className="font-serif tabular-nums" style={{ fontSize: "2rem", fontWeight: 300, letterSpacing: "-0.02em", color: "#A18048" }}>
            {fmtEUR(pancia.provvigioni || 0)}
          </p>
          <div className="mt-4 pt-4 border-t" style={{ borderColor: "#F0F2F5" }}>
            <div className="flex items-center justify-between text-[12px]">
              <span className="text-slate-400">{pancia.count || 0} pratiche aperte</span>
              <span className="text-slate-500 tabular-nums">Forecast {fmtEUR(pancia.forecast || 0)}</span>
            </div>
          </div>
        </Surface>

        {/* Goal tracker */}
        <GoalTrackerCard />
      </div>

      {/* ── MAIN CONTENT ROW ── */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mt-6">

        {/* Pipeline — 2 cols */}
        <Surface testid="card-pipeline" className="lg:col-span-2" noPad>
          <div className="px-6 pt-6 pb-2">
            <SectionHeader title="Pipeline mensile" />
          </div>
          <div className="px-6 pb-6">
            <PipelineItem label="Prestiti Personali" cat="PP"  data={data.pipeline?.PP}  />
            <PipelineItem label="Cessione del Quinto" cat="CQ" data={data.pipeline?.CQ}  />
            <PipelineItem label="Liquidate"           cat="LIQ" data={data.pipeline?.LIQ} last />
          </div>
        </Surface>

        {/* Trend chart — 3 cols */}
        <Surface testid="card-trend" className="lg:col-span-3" noPad>
          <div className="px-6 pt-6 pb-4">
            <SectionHeader
              title="Provvigioni liquidate"
              action={<span className="text-[11px] text-slate-400">Ultimi 6 mesi · {fmtEUR(trend.reduce((s, t) => s + (t.provvigioni || 0), 0))}</span>}
            />
          </div>
          <div className="px-2 pb-4" style={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trend} margin={{ top: 4, right: 20, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="goldGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"   stopColor="#B7955B" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#B7955B" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#F0F2F5" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" stroke="#CBD5E1" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#CBD5E1" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `€${(v / 1000).toFixed(0)}k`} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="provvigioni" stroke="#B7955B" strokeWidth={2} fill="url(#goldGrad)" dot={false} activeDot={{ r: 4, fill: "#B7955B", strokeWidth: 0 }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Surface>
      </div>

      {/* ── BOTTOM ROW: Hot clients + Appointments + Urgencies ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">

        {/* Hot clients */}
        <Surface testid="card-hot" noPad>
          <div className="px-6 pt-6 pb-2">
            <SectionHeader
              title="Clienti caldi"
              action={<Link to="/clienti" className="flex items-center gap-1 hover:opacity-70 transition-opacity">Tutti <ChevronRight className="w-3 h-3" /></Link>}
            />
          </div>
          <div className="px-6 pb-4">
            {(data.hotClients || []).length === 0 ? (
              <MiniEmpty variant="flame" text="Nessun cliente caldo al momento" />
            ) : (
              <div className="stagger stagger-fade">
              {data.hotClients.map((c, i) => (
                <HotClientRow key={c.id} c={c} last={i === data.hotClients.length - 1} />
              ))}
              </div>
            )}
          </div>
        </Surface>

        {/* Appointments */}
        <Surface testid="card-appointments" noPad>
          <div className="px-6 pt-6 pb-2">
            <SectionHeader
              title="Prossimi appuntamenti"
              action={<Link to="/agenda" className="flex items-center gap-1 hover:opacity-70 transition-opacity">Agenda <ChevronRight className="w-3 h-3" /></Link>}
            />
          </div>
          <div className="px-6 pb-4">
            {(data.upcomingAppointments || []).length === 0 ? (
              <MiniEmpty variant="calendar" text="Nessun appuntamento pianificato" />
            ) : (
              <div className="stagger stagger-fade">
              {data.upcomingAppointments.map((a, i) => (
                <AppointmentItem key={a.id} a={a} last={i === data.upcomingAppointments.length - 1} />
              ))}
              </div>
            )}
          </div>
        </Surface>

        {/* Urgent practices */}
        <Surface testid="card-urgent" noPad>
          <div className="px-6 pt-6 pb-2">
            <SectionHeader
              title="Pratiche urgenti"
              action={<Link to="/pipeline" className="flex items-center gap-1 hover:opacity-70 transition-opacity">Pipeline <ChevronRight className="w-3 h-3" /></Link>}
            />
          </div>
          <div className="px-6 pb-4">
            {(data.urgentPractices || []).length === 0 ? (
              <MiniEmpty variant="check" text="Nessuna pratica urgente. Tutto sotto controllo." />
            ) : (
              data.urgentPractices.map((p, i) => (
                <div key={p.id} className={`flex items-center justify-between py-4 ${i === data.urgentPractices.length - 1 ? "" : "border-b"}`} style={{ borderColor: "#F0F2F5" }}>
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] font-medium text-slate-800 truncate">{p.elemento || p.prodotto || "Pratica"}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <StatusBadge variant={p.cat === "LIQ" ? "success" : p.cat === "CQ" ? "info" : "gold"}>{p.cat}</StatusBadge>
                      <StatusBadge variant={statoPraticaVariant(p.stato)}>{p.stato}</StatusBadge>
                    </div>
                  </div>
                  <p className="text-[12px] text-slate-400 tabular-nums ml-4 shrink-0">{fmtDateIT(p.dataLimite)}</p>
                </div>
              ))
            )}
          </div>
        </Surface>
      </div>

      {/* ── COMANDANTE AI BRAIN (insight proattivi) ── */}
      <div className="mt-4">
        <BrainPanel />
      </div>

      {/* ── AI COACH ── */}
      <div className="mt-4">
        <CoachCard />
      </div>
    </AppLayout>
  );
}

/* ═══════════════════════════════════════════════
   AGENT DASHBOARD
═══════════════════════════════════════════════ */
function AgentDashboard({ user, kpi, switcher = null }) {
  const firstName = (user?.name || "").split(" ")[0] || "Agente";
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Buongiorno" : hour < 18 ? "Buon pomeriggio" : "Buonasera";

  return (
    <AppLayout
      title={`${greeting}, ${firstName}`}
      subtitle="I tuoi numeri in tempo reale"
      actions={
        <div className="flex items-center gap-2">
          {switcher}
          <Link to="/clienti?view=daily" data-testid="dash-cta-daily"
            className="text-white text-[13px] font-medium px-4 py-2 flex items-center gap-2 transition-colors"
            style={{ background: "var(--navy)", borderRadius: 3 }}
            onMouseEnter={e => e.currentTarget.style.background = "var(--navy-3)"}
            onMouseLeave={e => e.currentTarget.style.background = "var(--navy)"}>
            <Phone className="w-3.5 h-3.5" strokeWidth={1.6} /> Richiami di oggi
          </Link>
        </div>
      }
    >
      {/* Personal scope banner */}
      <div className="flex items-center gap-3 px-5 py-3.5 mb-6"
        style={{ background: "#FDFAF5", border: "1px solid rgba(183,149,91,0.3)", borderRadius: 4 }}
        data-testid="dash-scope-banner">
        <div className="w-8 h-8 rounded-full flex items-center justify-center text-[12px] font-semibold shrink-0"
          style={{ background: "var(--gold)", color: "var(--navy)" }}>
          {firstName.slice(0, 1).toUpperCase()}
        </div>
        <p className="text-[13px] text-slate-600">
          <span className="font-semibold text-slate-800">Dashboard personale</span> · {kpi.totalAssigned || 0} clienti assegnati a te
        </p>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 stagger animate-fade-in">
        <KpiCard testid="kpi-todayCalls"  label="Da chiamare oggi" value={kpi.todayCalls      || 0} sub={`Su ${kpi.totalAssigned || 0} clienti`} icon={Phone}         tone="#579BFC" to="/clienti?view=daily" />
        <KpiCard testid="kpi-expired"     label="Recall scaduti"   value={kpi.expiredRecalls  || 0} sub="Da recuperare"                           icon={AlertTriangle}  warn   to="/clienti?view=expired" />
        <KpiCard testid="kpi-hot"         label="Clienti caldi"    value={kpi.hot             || 0} sub="Interessati · Da proporre"               icon={Flame}          tone="#FDAB3D" to="/clienti?view=hot" />
        <KpiCard testid="kpi-liquid"      label="Liquidati 30gg"   value={kpi.liquidatedLast30d||0} sub="Mese corrente"                           icon={TrendingUp}     tone="#00C875" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 stagger animate-fade-in">
        <KpiCard testid="kpi-attesa-doc"  label="Attesa documenti" value={kpi.attesaDoc       || 0} sub="In raccolta"          icon={FileText}     tone="#4ECCC6" to="/clienti?view=attesaDoc" />
        <KpiCard testid="kpi-trattativa"  label="In trattativa"    value={kpi.inTrattativa    || 0} sub="Negoziazione"          icon={Handshake}    tone="#A25DDC" to="/clienti?view=trattativa" />
        <KpiCard testid="kpi-no-date"     label="Senza data"       value={kpi.noDate          || 0} sub="Da pianificare"        icon={Clock3}       tone="#FF158A" to="/clienti?view=noDate" />
        <KpiCard testid="kpi-total"       label="Tot. assegnati"   value={kpi.totalAssigned   || 0} sub="Pool personale"        icon={Users}        tone="#0086C0" to="/clienti" />
      </div>

      {/* Second KPI row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
        <KpiCard testid="kpi-appts-today" label="Appuntamenti oggi"   value={kpi.todayAppointments|| 0} sub="Agenda del giorno"              icon={CalendarClock} tone="#B7955B" to="/agenda" />
        <KpiCard testid="kpi-rem-overdue" label="Promemoria scaduti"  value={kpi.remindersOverdue || 0} sub={`Prossimi 7gg: ${kpi.remindersNext7 || 0}`} icon={Clock3} warn to="/agenda" />
        <KpiCard testid="kpi-active7"     label="Attivi (7 giorni)"   value={kpi.active7d         || 0} sub="Lavorati ultima settimana"     icon={UserCheck}      tone="#00C875" to="/clienti" />
      </div>

      {/* Shortcuts */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-3">
        <ShortcutCard to="/clienti"    icon={Users}    title="I miei clienti"     description="Lista completa dei clienti assegnati" testid="shortcut-clienti" />
        <ShortcutCard to="/comandante" icon={Sparkles} title="Comandante AI"      description="Cerca, scadenzia e aggiorna in linguaggio naturale" testid="shortcut-comandante" />
        <ShortcutCard to="/campagne"   icon={Megaphone} title="Campagne WhatsApp" description="Visualizza le campagne in cui sei coinvolto" testid="shortcut-campagne" />
      </div>
    </AppLayout>
  );
}

/* ═══════════════════════════════════════════════
   WA OPERATOR DASHBOARD
═══════════════════════════════════════════════ */
function WaOperatorDashboard({ user, viewAsId = null, switcher = null }) {
  const [items, setItems]   = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState("");
  const [filter, setFilter] = useState("all");
  const [savingId, setSavingId] = useState(null);

  const renderTemplate = useCallback((tpl, client) => {
    if (!tpl) return "";
    const c = client || {};
    return String(tpl)
      .replace(/\{\{?\s*nome\s*\}?\}/gi, c.nome || "")
      .replace(/\{\{?\s*cognome\s*\}?\}/gi, c.cognome || "")
      .replace(/\{\{?\s*telefono\s*\}?\}/gi, c.telefono || "")
      .replace(/\{\{?\s*citta\s*\}?\}/gi, c.citta || "");
  }, []);

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true); setError("");
    try {
      const params = viewAsId ? { viewAs: viewAsId } : {};
      const r = await api.get("/campaigns/me/inbox", { params });
      const data = r.data || {};
      setItems(data.items || []);
      const cc = (data.items || []).reduce((a, t) => ({ ...a, [t.status]: (a[t.status] || 0) + 1 }), {});
      setCounts(cc);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Errore caricamento");
    } finally { setLoading(false); }
  }, [user?.id, viewAsId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(() => load(), 30000); return () => clearInterval(t); }, [load]);

  const updateStatus = async (target, newStatus) => {
    if (target.status === newStatus) return;
    setSavingId(target.id);
    try {
      await api.put(`/campaigns/${target.campaign_id}/targets/${target.id}/status`, { status: newStatus });
      setItems((arr) => arr.map((t) => t.id === target.id ? { ...t, status: newStatus } : t));
      setCounts((cc) => { const next = { ...cc }; next[target.status] = Math.max(0, (next[target.status] || 1) - 1); next[newStatus] = (next[newStatus] || 0) + 1; return next; });
    } catch (e) { setError(e?.response?.data?.detail || "Errore aggiornamento"); }
    finally { setSavingId(null); }
  };

  const openWa = (target) => {
    const cl = target.client || {};
    const phone = (cl.telefono || "").replace(/[^\d+]/g, "");
    if (!phone) { setError("Telefono non disponibile."); return; }
    const text = renderTemplate(target.message_template, cl) || "";
    const intl = phone.startsWith("+") ? phone.slice(1) : (phone.startsWith("00") ? phone.slice(2) : phone);
    try { window.open(`https://wa.me/${intl}?text=${encodeURIComponent(text)}`, "_blank", "noopener"); } catch {}
    if (target.status === "pending") updateStatus(target, "INVIATO");
  };

  const total   = items.length;
  const done    = ["INVIATO", "LETTO", "RISPOSTO"].reduce((a, k) => a + (counts[k] || 0), 0);
  const pending = (counts.pending || 0);
  const filtered = items.filter((t) => {
    if (filter === "pending") return t.status === "pending";
    if (filter === "done")    return ["INVIATO", "LETTO", "RISPOSTO"].includes(t.status);
    return true;
  });

  const STATUS_TONE = {
    pending:   { label: "Da contattare", color: "bg-slate-100 text-slate-700 border-slate-200" },
    INVIATO:   { label: "Inviato",       color: "bg-sky-50 text-sky-700 border-sky-200" },
    LETTO:     { label: "Letto",         color: "bg-violet-50 text-violet-700 border-violet-200" },
    RISPOSTO:  { label: "Risposto",      color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    NO_ANSWER: { label: "No-answer",     color: "bg-amber-50 text-amber-700 border-amber-200" },
    FALLITO:   { label: "Fallito",       color: "bg-rose-50 text-rose-700 border-rose-200" },
    OPT_OUT:   { label: "Opt-out",       color: "bg-rose-50 text-rose-700 border-rose-200" },
  };

  return (
    <AppLayout
      title={viewAsId ? `Vista: ${user?.name || user?.email || "Operatore"}` : `Ciao ${user?.name || "Operatore"}`}
      subtitle={`${total} ${total === 1 ? "contatto" : "contatti"} in coda`}
      actions={switcher}
    >
      {loading && total === 0 ? (
        <div data-testid="wa-op-loading"><LoadingBlock variant="page" /></div>
      ) : error && total === 0 ? (
        <div className="p-4 text-[13px] text-rose-600" style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 4 }} data-testid="wa-op-error">
          {error}
          <button onClick={load} className="ml-3 underline text-rose-500">Riprova</button>
        </div>
      ) : total === 0 ? (
        <div data-testid="wa-op-empty">
          <EmptyState
            variant="chat"
            title="Nessun contatto in coda"
            description="Quando l'amministratore ti assegnerà contatti da lavorare, li troverai qui organizzati per stato e priorità."
          />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="wa-op-counters">
            <KpiCard label="Nominativi"    value={total}              icon={Users}   testid="kpi-total" tone="#0086C0" />
            <KpiCard label="Da contattare" value={pending}            icon={Clock3}  testid="kpi-pending" warn={pending > 0} />
            <KpiCard label="Lavorati"      value={done}               icon={Phone}   testid="kpi-done" tone="#00C875" />
            <KpiCard label="Risposti"      value={counts.RISPOSTO||0} icon={Flame}   testid="kpi-replied" tone="#FDAB3D" />
          </div>

          <Surface noPad testid="wa-op-table">
            <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: "#F0F2F5" }}>
              <div className="flex items-center gap-1">
                {[{ v: "all", label: `Tutti (${total})` }, { v: "pending", label: `Da contattare (${pending})` }, { v: "done", label: `Lavorati (${done})` }].map((opt) => (
                  <button key={opt.v} onClick={() => setFilter(opt.v)} data-testid={`wa-op-filter-${opt.v}`}
                    className="text-[12px] font-medium px-3 py-1.5 transition-colors"
                    style={{ borderRadius: 3, background: filter === opt.v ? "var(--navy)" : "transparent", color: filter === opt.v ? "#fff" : "#64748B" }}>
                    {opt.label}
                  </button>
                ))}
              </div>
              <button onClick={load} className="text-[12px] text-slate-400 hover:text-slate-700 flex items-center gap-1.5 transition-colors">
                <ArrowRight className="w-3 h-3" /> Aggiorna
              </button>
            </div>
            <div className="overflow-auto max-h-[60vh]">
              <table className="w-full text-sm">
                <thead style={{ background: "#FAFBFC" }}>
                  <tr className="border-b" style={{ borderColor: "#F0F2F5" }}>
                    {["Cliente", "Telefono", "Stato", "Azione", "Cambia stato"].map(h => (
                      <th key={h} className="text-left px-5 py-3 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((t) => {
                    const tone = STATUS_TONE[t.status] || STATUS_TONE.pending;
                    const cl   = t.client || {};
                    const hasPhone = !!(cl.telefono || "").trim();
                    return (
                      <tr key={t.id} className="border-b hover:bg-slate-50/60 transition-colors" style={{ borderColor: "#F0F2F5" }} data-testid={`wa-op-row-${t.id}`}>
                        <td className="px-5 py-3.5">
                          <ClientLink clientId={t.client_id} variant="strong" testIdPrefix={`wa-op-client-link-${t.id}`}>
                            <span className="text-[13px] font-medium text-slate-800">{cl.cognome || ""} {cl.nome || ""}</span>
                          </ClientLink>
                          {cl.citta && <p className="text-[11px] text-slate-400 mt-0.5">{cl.citta}</p>}
                        </td>
                        <td className="px-5 py-3.5 font-mono text-[12px] tabular-nums text-slate-600">
                          {cl.telefono ? <a href={`tel:${cl.telefono}`} className="hover:text-slate-900">{cl.telefono}</a> : "—"}
                        </td>
                        <td className="px-5 py-3.5">
                          <span className={`inline-flex items-center text-[11px] font-medium px-2.5 py-1 border ${tone.color}`} style={{ borderRadius: 3 }}>{tone.label}</span>
                        </td>
                        <td className="px-5 py-3.5">
                          <button onClick={() => openWa(t)} disabled={!hasPhone} data-testid={`wa-op-send-btn-${t.id}`}
                            className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            style={{ background: "#25D366", borderRadius: 3 }}
                            onMouseEnter={e => { if (!e.currentTarget.disabled) e.currentTarget.style.background = "#1ea952"; }}
                            onMouseLeave={e => e.currentTarget.style.background = "#25D366"}>
                            <MessageCircle className="w-3.5 h-3.5" />
                            {t.status === "pending" ? "Invia" : "Riapri"}
                          </button>
                        </td>
                        <td className="px-5 py-3.5">
                          <select value={t.status} onChange={(e) => updateStatus(t, e.target.value)} disabled={savingId === t.id}
                            data-testid={`wa-op-status-select-${t.id}`}
                            className="text-[12px] px-2.5 py-1.5 border border-slate-200 bg-white disabled:opacity-50 transition-colors"
                            style={{ borderRadius: 3 }}>
                            {Object.entries(STATUS_TONE).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                          </select>
                          {savingId === t.id && <Loader2 className="w-3 h-3 animate-spin inline-block ml-2 text-slate-400" />}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Surface>
        </>
      )}
    </AppLayout>
  );
}


/**
 * MiniEmpty — placeholder elegante per card piccole della dashboard.
 * Non usa EmptyState (troppo grande) — versione mini con icona gold circolare.
 */
function MiniEmpty({ variant = "check", text }) {
  const iconPath = {
    flame: (
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <path d="M8.5 14.5A2.5 2.5 0 0011 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 11-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 002.5 2.5z"/>
      </svg>
    ),
    calendar: (
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
      </svg>
    ),
    check: (
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/>
      </svg>
    ),
  }[variant];

  return (
    <div className="animate-empty-in flex flex-col items-center justify-center py-8 text-center">
      <div className="w-10 h-10 rounded-full flex items-center justify-center mb-2"
           style={{ background: 'rgba(183,149,91,0.10)', color: 'var(--gold)' }}>
        {iconPath}
      </div>
      <p className="text-[12px] text-slate-500 max-w-xs">{text}</p>
    </div>
  );
}
